<script setup lang="ts">
import { PhFileDoc, PhFilePdf, PhFolder } from '@phosphor-icons/vue';

/**
 * Purely decorative stand-in for the file listing that *would* be here — a faded folders-then-files
 * stack that dissolves downward, so a whole-pane notice reads as an empty file manager rather than
 * an empty box.
 *
 * Deliberately static, unlike DetailsTableSkeleton's pulsing bars: a pulse promises that content is
 * on its way, and most notices (unprovisioned, no access, load failed) are saying the opposite. Real
 * Phosphor icons rather than the skeleton's grey squares, and folders before files, because the point
 * is to look like *this* file manager's listing at a glance.
 */
const ROWS = [
  { icon: PhFolder, nameWidth: '9rem', metaWidth: '3.5rem' },
  { icon: PhFolder, nameWidth: '6.5rem', metaWidth: '4.5rem' },
  { icon: PhFilePdf, nameWidth: '11rem', metaWidth: '3rem' },
  { icon: PhFileDoc, nameWidth: '8rem', metaWidth: '4rem' },
];
</script>

<template>
  <!-- aria-hidden: decoration only. The notice's real message is the accessible content. -->
  <div class="ghost-file-rows" aria-hidden="true">
    <div v-for="(row, index) in ROWS" :key="index" class="ghost-row">
      <component :is="row.icon" :size="20" weight="fill" class="ghost-icon" />
      <span class="ghost-bar" :style="{ width: row.nameWidth }" />
      <span class="ghost-bar ghost-bar-meta" :style="{ width: row.metaWidth }" />
    </div>
  </div>
</template>

<style scoped>
.ghost-file-rows {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 22rem;
  /* Fades out downward so the stack reads as receding decoration rather than as rows the user might
     try to click, and so the badge/message below it have somewhere clean to sit. */
  mask-image: linear-gradient(to bottom, #000 25%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, #000 25%, transparent 100%);
}

.ghost-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.375rem;
  background-color: #f7f8fa;
}

.ghost-icon {
  flex-shrink: 0;
  color: #cbd2da;
}

.ghost-bar {
  height: 0.5rem;
  border-radius: 9999px;
  background-color: #e4e7eb;
}

.ghost-bar-meta {
  /* Right-aligned like the real table's Modified/Size columns. */
  margin-left: auto;
}
</style>
